import { CanvasLayerCache } from "./canvasLayerCache";
import type { LobbyPelletView, LobbyPositionView } from "../../shared/game";
import { SITE_TUNING } from "./siteTuning";

type LobbySplash = {
    position: LobbyPositionView;
    startedAt: number;
};

type LobbyMoveTarget = {
    position: LobbyPositionView;
    startedAt: number;
};

type LobbyHighFive = {
    position: LobbyPositionView;
    avatarHues: readonly [number, number];
    startedAt: number;
};

type LobbyViewport = {
    width: number;
    height: number;
    worldWidth: number;
    worldHeight: number;
    cameraLeft: number;
    cameraTop: number;
};

type LobbyCanvasThemeName = "default" | "relic" | "pictionary" | "vault" | "planet";

type LobbyCanvasTheme = {
    baseTop: string;
    baseBottom: string;
    glowPrimary: string;
    glowSecondary: string;
    sweep: string;
    grid: string;
    topShade: string;
    bottomShade: string;
    vignette: string;
    pelletHaloCore: string;
    pelletHaloMid: string;
    pelletHaloEdge: string;
    pelletCore: string;
};

const LOBBY_CANVAS_THEMES: Record<LobbyCanvasThemeName, LobbyCanvasTheme> = {
    default: {
        baseTop: "rgba(18, 57, 84, 0.92)",
        baseBottom: "rgba(8, 28, 43, 0.98)",
        glowPrimary: "rgba(95, 199, 255, 0.16)",
        glowSecondary: "rgba(255, 255, 255, 0.1)",
        sweep: "rgba(255, 255, 255, 0.04)",
        grid: "rgba(255, 255, 255, 0.04)",
        topShade: "rgba(4, 10, 18, 0.32)",
        bottomShade: "rgba(4, 10, 18, 0.38)",
        vignette: "rgba(0, 0, 0, 0.18)",
        pelletHaloCore: "rgba(255, 255, 255, 0.96)",
        pelletHaloMid: "rgba(255, 255, 255, 0.52)",
        pelletHaloEdge: "rgba(255, 255, 255, 0.14)",
        pelletCore: "rgba(255, 255, 255, 0.97)"
    },
    relic: {
        baseTop: "rgba(22, 39, 46, 0.93)",
        baseBottom: "rgba(10, 15, 20, 0.98)",
        glowPrimary: "rgba(255, 203, 111, 0.11)",
        glowSecondary: "rgba(255, 232, 182, 0.08)",
        sweep: "rgba(255, 231, 178, 0.035)",
        grid: "rgba(255, 232, 182, 0.038)",
        topShade: "rgba(5, 10, 15, 0.3)",
        bottomShade: "rgba(5, 10, 15, 0.36)",
        vignette: "rgba(0, 0, 0, 0.19)",
        pelletHaloCore: "rgba(255, 248, 222, 0.98)",
        pelletHaloMid: "rgba(255, 204, 106, 0.56)",
        pelletHaloEdge: "rgba(223, 157, 59, 0.22)",
        pelletCore: "rgba(255, 239, 194, 0.98)"
    },
    pictionary: {
        baseTop: "rgba(42, 50, 54, 0.94)",
        baseBottom: "rgba(13, 19, 23, 0.98)",
        glowPrimary: "rgba(255, 255, 250, 0.08)",
        glowSecondary: "rgba(213, 179, 93, 0.11)",
        sweep: "rgba(255, 255, 250, 0.035)",
        grid: "rgba(243, 240, 229, 0.052)",
        topShade: "rgba(5, 8, 10, 0.3)",
        bottomShade: "rgba(5, 8, 10, 0.38)",
        vignette: "rgba(0, 0, 0, 0.2)",
        pelletHaloCore: "rgba(255, 253, 243, 0.98)",
        pelletHaloMid: "rgba(213, 179, 93, 0.52)",
        pelletHaloEdge: "rgba(99, 88, 65, 0.3)",
        pelletCore: "rgba(255, 250, 228, 0.99)"
    },
    vault: {
        baseTop: "rgba(24, 20, 31, 0.95)",
        baseBottom: "rgba(8, 7, 12, 0.99)",
        glowPrimary: "rgba(184, 119, 255, 0.1)",
        glowSecondary: "rgba(232, 222, 255, 0.07)",
        sweep: "rgba(232, 222, 255, 0.028)",
        grid: "rgba(232, 222, 255, 0.035)",
        topShade: "rgba(4, 3, 7, 0.34)",
        bottomShade: "rgba(4, 3, 7, 0.42)",
        vignette: "rgba(0, 0, 0, 0.24)",
        pelletHaloCore: "rgba(248, 244, 255, 0.96)",
        pelletHaloMid: "rgba(184, 119, 255, 0.5)",
        pelletHaloEdge: "rgba(85, 58, 118, 0.26)",
        pelletCore: "rgba(246, 241, 255, 0.98)"
    },
    planet: {
        baseTop: "rgba(8, 15, 25, 0.98)",
        baseBottom: "rgba(1, 3, 7, 1)",
        glowPrimary: "rgba(89, 128, 171, 0.09)",
        glowSecondary: "rgba(188, 214, 235, 0.045)",
        sweep: "rgba(184, 215, 238, 0.018)",
        grid: "rgba(175, 202, 224, 0.032)",
        topShade: "rgba(1, 3, 7, 0.4)",
        bottomShade: "rgba(1, 3, 7, 0.54)",
        vignette: "rgba(0, 0, 0, 0.3)",
        pelletHaloCore: "rgba(242, 249, 255, 0.98)",
        pelletHaloMid: "rgba(169, 207, 240, 0.46)",
        pelletHaloEdge: "rgba(107, 151, 193, 0.16)",
        pelletCore: "rgba(240, 249, 255, 0.98)"
    }
};

const GRID_CELL_SIZE_PX = 36;
const SPLASH_DURATION_MS = 560;
const MOVE_TARGET_DURATION_MS = SITE_TUNING.lobby.feedback.moveTargetDurationMs;
const HIGH_FIVE_DURATION_MS = SITE_TUNING.lobby.feedback.highFiveEffectDurationMs;
const MAX_DEVICE_PIXEL_RATIO = 2;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const mix = (from: number, to: number, amount: number): number => from + (to - from) * amount;

const easeOut = (value: number): number => 1 - Math.pow(1 - clamp(value, 0, 1), 3);

export class LobbyCanvasRenderer {
    private readonly canvas: HTMLCanvasElement;
    private readonly context: CanvasRenderingContext2D | null;
    readonly isAvailable: boolean;
    private readonly backdropCache = new CanvasLayerCache();
    private readonly shadeCache = new CanvasLayerCache();
    private readonly primaryGlowCache = new CanvasLayerCache();
    private readonly secondaryGlowCache = new CanvasLayerCache();
    private readonly pelletHaloCache = new CanvasLayerCache();
    private cssWidth = 0;
    private cssHeight = 0;
    private worldWidth = 0;
    private worldHeight = 0;
    private cameraLeft = 0;
    private cameraTop = 0;
    private pellets: LobbyPelletView[] = [];
    private splashes: LobbySplash[] = [];
    private moveTargets: LobbyMoveTarget[] = [];
    private highFives: LobbyHighFive[] = [];
    private theme: LobbyCanvasTheme = LOBBY_CANVAS_THEMES.default;
    private themeName: LobbyCanvasThemeName = "default";

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.context = canvas.getContext("2d");
        this.isAvailable = this.context !== null;
    }

    destroy() {
        this.backdropCache.destroy();
        this.shadeCache.destroy();
        this.primaryGlowCache.destroy();
        this.secondaryGlowCache.destroy();
        this.pelletHaloCache.destroy();
        this.pellets = [];
        this.splashes = [];
        this.moveTargets = [];
        this.highFives = [];
    }

    clearTransientState() {
        this.splashes = [];
        this.moveTargets = [];
        this.highFives = [];
    }

    setTheme(themeName: LobbyCanvasThemeName) {
        if (this.themeName === themeName) {
            return;
        }

        this.themeName = themeName;
        this.theme = LOBBY_CANVAS_THEMES[themeName];
    }

    setPellets(pellets: LobbyPelletView[]) {
        this.pellets = pellets.map((pellet) => ({
            id: pellet.id,
            x: pellet.x,
            y: pellet.y
        }));
    }

    setViewport(viewport: LobbyViewport) {
        const nextWidth = Math.max(1, Math.round(viewport.width));
        const nextHeight = Math.max(1, Math.round(viewport.height));

        if (this.cssWidth === nextWidth && this.cssHeight === nextHeight
            && this.canvas.width === Math.round(nextWidth * this.getDevicePixelRatio())
            && this.canvas.height === Math.round(nextHeight * this.getDevicePixelRatio())) {
            this.worldWidth = Math.max(nextWidth, Math.round(viewport.worldWidth));
            this.worldHeight = Math.max(nextHeight, Math.round(viewport.worldHeight));
            this.cameraLeft = viewport.cameraLeft;
            this.cameraTop = viewport.cameraTop;
            return;
        }

        this.cssWidth = nextWidth;
        this.cssHeight = nextHeight;
        this.worldWidth = Math.max(nextWidth, Math.round(viewport.worldWidth));
        this.worldHeight = Math.max(nextHeight, Math.round(viewport.worldHeight));
        this.cameraLeft = viewport.cameraLeft;
        this.cameraTop = viewport.cameraTop;

        const devicePixelRatio = this.getDevicePixelRatio();
        const pixelWidth = Math.max(1, Math.round(nextWidth * devicePixelRatio));
        const pixelHeight = Math.max(1, Math.round(nextHeight * devicePixelRatio));

        if (this.canvas.width !== pixelWidth) {
            this.canvas.width = pixelWidth;
        }

        if (this.canvas.height !== pixelHeight) {
            this.canvas.height = pixelHeight;
        }
    }

    emitPelletSplash(position: LobbyPositionView, startedAt = performance.now()) {
        this.splashes.push({
            position: {
                x: position.x,
                y: position.y
            },
            startedAt
        });
    }

    emitMoveTarget(position: LobbyPositionView, startedAt = performance.now()) {
        this.moveTargets = [
            {
                position: { ...position },
                startedAt
            }
        ];
    }

    emitHighFive(
        position: LobbyPositionView,
        avatarHues: readonly [number, number],
        startedAt = performance.now()
    ) {
        this.highFives.push({
            position: { ...position },
            avatarHues,
            startedAt
        });
    }

    hasActiveEffects(recordedAt = performance.now()): boolean {
        return (
            this.splashes.some((splash) => recordedAt - splash.startedAt < SPLASH_DURATION_MS) ||
            this.moveTargets.some(
                (target) => recordedAt - target.startedAt < MOVE_TARGET_DURATION_MS
            ) ||
            this.highFives.some(
                (highFive) => recordedAt - highFive.startedAt < HIGH_FIVE_DURATION_MS
            )
        );
    }

    render(recordedAt = performance.now()) {
        const context = this.context;

        if (!context || this.cssWidth <= 0 || this.cssHeight <= 0) {
            return;
        }

        this.splashes = this.splashes.filter((splash) => recordedAt - splash.startedAt < SPLASH_DURATION_MS);
        this.moveTargets = this.moveTargets.filter(
            (target) => recordedAt - target.startedAt < MOVE_TARGET_DURATION_MS
        );
        this.highFives = this.highFives.filter(
            (highFive) => recordedAt - highFive.startedAt < HIGH_FIVE_DURATION_MS
        );

        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        context.setTransform(this.getDevicePixelRatio(), 0, 0, this.getDevicePixelRatio(), 0, 0);
        context.imageSmoothingEnabled = true;

        this.drawBackdrop(context, recordedAt);
        this.drawMoveTargets(context, recordedAt);
        this.drawPellets(context, recordedAt);
        this.drawSplashes(context, recordedAt);
        this.drawHighFives(context, recordedAt);
    }

    private drawMoveTargets(context: CanvasRenderingContext2D, recordedAt: number) {
        const worldWidth = Math.max(this.cssWidth, this.worldWidth);
        const worldHeight = Math.max(this.cssHeight, this.worldHeight);

        for (const target of this.moveTargets) {
            const progress = clamp((recordedAt - target.startedAt) / MOVE_TARGET_DURATION_MS, 0, 1);
            const eased = easeOut(progress);
            const centerX = target.position.x * worldWidth - this.cameraLeft;
            const centerY = target.position.y * worldHeight - this.cameraTop;
            const radius = mix(24, 10, eased);
            const alpha = (1 - progress) * 0.78;

            context.save();
            context.strokeStyle = `rgba(168, 255, 243, ${alpha})`;
            context.lineWidth = mix(2.4, 1.2, eased);
            context.beginPath();
            context.arc(centerX, centerY, radius, 0, Math.PI * 2);
            context.stroke();

            context.fillStyle = `rgba(220, 255, 250, ${alpha * 0.82})`;
            context.beginPath();
            context.arc(centerX, centerY, mix(3.2, 1.8, eased), 0, Math.PI * 2);
            context.fill();
            context.restore();
        }
    }

    private drawHighFives(context: CanvasRenderingContext2D, recordedAt: number) {
        const worldWidth = Math.max(this.cssWidth, this.worldWidth);
        const worldHeight = Math.max(this.cssHeight, this.worldHeight);

        for (const highFive of this.highFives) {
            const progress = clamp((recordedAt - highFive.startedAt) / HIGH_FIVE_DURATION_MS, 0, 1);
            const eased = easeOut(progress);
            const centerX = highFive.position.x * worldWidth - this.cameraLeft;
            const centerY = highFive.position.y * worldHeight - this.cameraTop;
            const alpha = Math.pow(1 - progress, 0.8);

            context.save();
            context.globalCompositeOperation = "screen";
            context.lineCap = "round";
            context.lineWidth = mix(3.2, 1.2, eased);

            for (let index = 0; index < 10; index += 1) {
                const angle = (index / 10) * Math.PI * 2 + progress * 0.24;
                const innerRadius = mix(6, 28, eased);
                const outerRadius = mix(18, 72, eased);
                const hue = highFive.avatarHues[index % 2];

                context.strokeStyle = this.getAvatarEffectColor(hue, alpha * 0.9);
                context.beginPath();
                context.moveTo(
                    centerX + Math.cos(angle) * innerRadius,
                    centerY + Math.sin(angle) * innerRadius
                );
                context.lineTo(
                    centerX + Math.cos(angle) * outerRadius,
                    centerY + Math.sin(angle) * outerRadius
                );
                context.stroke();

                context.fillStyle = this.getAvatarEffectColor(hue, alpha * 0.78);
                context.beginPath();
                context.arc(
                    centerX + Math.cos(angle) * outerRadius,
                    centerY + Math.sin(angle) * outerRadius,
                    mix(3.6, 1.2, eased),
                    0,
                    Math.PI * 2
                );
                context.fill();
            }

            const ringRadius = mix(8, 58, eased);
            context.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.72})`;
            context.lineWidth = mix(3.4, 1, eased);
            context.beginPath();
            context.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
            context.stroke();

            const flash = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, 32);
            flash.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
            flash.addColorStop(0.34, `rgba(255, 255, 255, ${alpha * 0.46})`);
            flash.addColorStop(1, "rgba(255, 255, 255, 0)");
            context.fillStyle = flash;
            context.beginPath();
            context.arc(centerX, centerY, 32, 0, Math.PI * 2);
            context.fill();
            context.restore();
        }
    }

    private getAvatarEffectColor(avatarHue: number, alpha: number): string {
        if (avatarHue < 0) {
            return `rgba(248, 252, 255, ${alpha})`;
        }

        return `hsl(${avatarHue} 94% 72% / ${alpha})`;
    }

    private getDevicePixelRatio(): number {
        if (typeof window === "undefined") {
            return 1;
        }

        return clamp(window.devicePixelRatio || 1, 1, MAX_DEVICE_PIXEL_RATIO);
    }

    private drawBackdrop(context: CanvasRenderingContext2D, recordedAt: number) {
        const width = this.cssWidth;
        const height = this.cssHeight;
        const worldWidth = Math.max(width, this.worldWidth);
        const worldHeight = Math.max(height, this.worldHeight);
        const cameraLeft = this.cameraLeft;
        const cameraTop = this.cameraTop;

        // Screen-space layers do not depend on the camera. Keep their bitmaps while moving.
        const cacheKey = this.themeName;
        this.backdropCache.draw(context, width, height, this.getDevicePixelRatio(), cacheKey, (context) => {
            const baseGradient = context.createLinearGradient(0, 0, 0, height);
            baseGradient.addColorStop(0, this.theme.baseTop);
            baseGradient.addColorStop(1, this.theme.baseBottom);
            context.fillStyle = baseGradient;
            context.fillRect(0, 0, width, height);

        });

        this.drawCachedGlow(context, this.primaryGlowCache,
            worldWidth * 0.2 - cameraLeft, worldHeight * 0.2 - cameraTop,
            Math.max(worldWidth, worldHeight) * 0.28, this.theme.glowPrimary);
        this.drawCachedGlow(context, this.secondaryGlowCache,
            worldWidth * 0.78 - cameraLeft, worldHeight * 0.82 - cameraTop,
            Math.max(worldWidth, worldHeight) * 0.22, this.theme.glowSecondary);

        const sweepX = (((recordedAt * 0.018) + cameraLeft) % (worldWidth + height * 0.8)) - height * 0.4;
        const sweepGradient = context.createLinearGradient(sweepX, 0, sweepX + height * 0.45, 0);
        sweepGradient.addColorStop(0, "rgba(255, 255, 255, 0)");
        sweepGradient.addColorStop(0.5, this.theme.sweep);
        sweepGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
        context.fillStyle = sweepGradient;
        context.fillRect(0, 0, width, height);

        // Only the thin grid lines move; the expensive viewport shading stays cached.
        context.save();
        context.strokeStyle = this.theme.grid;
        context.lineWidth = 1;
        for (let x = -(cameraLeft % GRID_CELL_SIZE_PX); x <= width + GRID_CELL_SIZE_PX; x += GRID_CELL_SIZE_PX) {
            context.beginPath();
            context.moveTo(x + 0.5, 0);
            context.lineTo(x + 0.5, height);
            context.stroke();
        }
        for (let y = -(cameraTop % GRID_CELL_SIZE_PX); y <= height + GRID_CELL_SIZE_PX; y += GRID_CELL_SIZE_PX) {
            context.beginPath();
            context.moveTo(0, y + 0.5);
            context.lineTo(width, y + 0.5);
            context.stroke();
        }
        context.restore();

        this.shadeCache.draw(context, width, height, this.getDevicePixelRatio(), cacheKey, (context) => {
            const topShade = context.createLinearGradient(0, 0, 0, height * 0.2);
            topShade.addColorStop(0, this.theme.topShade);
            topShade.addColorStop(1, "rgba(4, 10, 18, 0)");
            context.fillStyle = topShade;
            context.fillRect(0, 0, width, height * 0.22);

            const bottomShade = context.createLinearGradient(0, height, 0, height * 0.78);
            bottomShade.addColorStop(0, this.theme.bottomShade);
            bottomShade.addColorStop(1, "rgba(4, 10, 18, 0)");
            context.fillStyle = bottomShade;
            context.fillRect(0, height * 0.74, width, height * 0.26);

            const vignette = context.createRadialGradient(
                width * 0.5,
                height * 0.5,
                Math.min(width, height) * 0.2,
                width * 0.5,
                height * 0.5,
                Math.max(width, height) * 0.78
            );
            vignette.addColorStop(0, "rgba(0, 0, 0, 0)");
            vignette.addColorStop(1, this.theme.vignette);
            context.fillStyle = vignette;
            context.fillRect(0, 0, width, height);
        });
    }

    private drawPellets(context: CanvasRenderingContext2D, recordedAt: number) {
        const worldWidth = Math.max(this.cssWidth, this.worldWidth);
        const worldHeight = Math.max(this.cssHeight, this.worldHeight);
        const cameraLeft = this.cameraLeft;
        const cameraTop = this.cameraTop;

        for (let index = 0; index < this.pellets.length; index += 1) {
            const pellet = this.pellets[index];
            const centerX = pellet.x * worldWidth - cameraLeft;
            const centerY = pellet.y * worldHeight - cameraTop;
            if (centerX < -14 || centerY < -14 || centerX > this.cssWidth + 14 || centerY > this.cssHeight + 14) {
                continue;
            }
            const pulse = 0.82 + 0.18 * Math.sin(recordedAt * 0.008 + index * 0.9);
            const haloRadius = mix(10, 14, pulse);
            const coreRadius = mix(4.6, 5.4, pulse);

            // Share one small glow bitmap across every pellet and pulse size.
            context.save();
            context.translate(centerX - haloRadius, centerY - haloRadius);
            context.scale(haloRadius / 14, haloRadius / 14);
            this.pelletHaloCache.draw(context, 28, 28, this.getDevicePixelRatio(), this.themeName, (layer) => {
                const halo = layer.createRadialGradient(14, 14, 0, 14, 14, 14);
                halo.addColorStop(0, this.theme.pelletHaloCore);
                halo.addColorStop(0.3, this.theme.pelletHaloMid);
                halo.addColorStop(0.7, this.theme.pelletHaloEdge);
                halo.addColorStop(1, "rgba(255, 255, 255, 0)");
                layer.fillStyle = halo;
                layer.fillRect(0, 0, 28, 28);
            });
            context.restore();

            context.fillStyle = this.theme.pelletCore;
            context.beginPath();
            context.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
            context.fill();
        }
    }

    private drawSplashes(context: CanvasRenderingContext2D, recordedAt: number) {
        const worldWidth = Math.max(this.cssWidth, this.worldWidth);
        const worldHeight = Math.max(this.cssHeight, this.worldHeight);
        const cameraLeft = this.cameraLeft;
        const cameraTop = this.cameraTop;

        for (const splash of this.splashes) {
            const progress = clamp((recordedAt - splash.startedAt) / SPLASH_DURATION_MS, 0, 1);
            const eased = easeOut(progress);
            const centerX = splash.position.x * worldWidth - cameraLeft;
            const centerY = splash.position.y * worldHeight - cameraTop;
            const radius = mix(7, 28, eased);
            const alpha = 1 - eased;

            context.save();

            const bloom = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
            bloom.addColorStop(0, `rgba(255, 255, 255, ${0.96 * alpha})`);
            bloom.addColorStop(0.4, `rgba(255, 255, 255, ${0.34 * alpha})`);
            bloom.addColorStop(1, "rgba(255, 255, 255, 0)");
            context.fillStyle = bloom;
            context.beginPath();
            context.arc(centerX, centerY, radius, 0, Math.PI * 2);
            context.fill();

            const crossLength = mix(10, 24, eased);
            const crossAlpha = 0.8 * alpha;
            context.strokeStyle = `rgba(255, 255, 255, ${crossAlpha})`;
            context.lineWidth = mix(2.2, 1.1, eased);
            context.lineCap = "round";
            context.translate(centerX, centerY);
            context.rotate(progress * 0.7);

            this.drawCross(context, crossLength);
            context.rotate(Math.PI / 4);
            this.drawCross(context, crossLength * 0.82);

            context.restore();
        }
    }

    private drawCross(context: CanvasRenderingContext2D, length: number) {
        context.beginPath();
        context.moveTo(-length, 0);
        context.lineTo(length, 0);
        context.moveTo(0, -length);
        context.lineTo(0, length);
        context.stroke();
    }

    private drawCachedGlow(
        context: CanvasRenderingContext2D,
        cache: CanvasLayerCache,
        x: number,
        y: number,
        radius: number,
        color: string
    ) {
        // Soft ambient light needs no world-sized bitmap (256 KiB per glow).
        context.save();
        context.translate(x - radius, y - radius);
        context.scale(radius / 128, radius / 128);
        cache.draw(context, 256, 256, 1, color, (layer) => {
            const glow = layer.createRadialGradient(128, 128, 0, 128, 128, 128);
            glow.addColorStop(0, color);
            glow.addColorStop(1, "rgba(255, 255, 255, 0)");
            layer.fillStyle = glow;
            layer.fillRect(0, 0, 256, 256);
        });
        context.restore();
    }
}
